'use client';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { getDefaultUrlForTosOrGdprLocalized } from '@eliadmin/core/src/utils/misc-utils';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Link from '@eliadmin/core/src/components/link';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import type { Country } from '@eliadmin/core/src/api/general/types/general-types';
import { Field } from '../../form/field';

const registerJobSeekerSchema = (VM: FormValidationMessages) =>
  z
    .object({
      name: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      surname: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      register_email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
      phone: z.string().min(12, VM.CANT_BE_EMPTY_PHONE_VALIDATION_MESSAGE()),
      password: z.string().min(8, VM.MIN_LENGTH_VALIDATION_MESSAGE(8)),
      password_confirmation: z.string().min(8, VM.MIN_LENGTH_VALIDATION_MESSAGE(8)),
      gdpr: z.boolean().refine(val => val === true, { message: VM.GDPR_VALIDATION_MESSAGE() }),
      newsletter: z.boolean().optional(),
      tos: z.boolean().refine(val => val === true, { message: VM.TOS_VALIDATION_MESSAGE() }),
      'g-recaptcha-response': z.string().default(''),
    })
    .refine(data => data.password === data.password_confirmation, {
      message: VM.PASSWORD_CONFIRMATION_DONT_MATCH_VALIDATION_MESSAGE(),
      path: ['password_confirmation'],
    });

interface RegisterFormJobSeekerProps {
  onSubmit: (formData: any, userType: 'jobseeker' | 'employer') => Promise<void>;
  showPassword: boolean;
  showPasswordConfirmation: boolean;
  onPasswordMouseDown: () => void;
  onPasswordMouseUp: () => void;
  onPasswordConfirmationMouseDown: () => void;
  onPasswordConfirmationMouseUp: () => void;
  countries: Country[];
}

export function RegisterFormJobSeeker({
  onSubmit,
  showPassword,
  showPasswordConfirmation,
  onPasswordMouseDown,
  onPasswordMouseUp,
  onPasswordConfirmationMouseDown,
  onPasswordConfirmationMouseUp,
  countries: _countries,
}: RegisterFormJobSeekerProps) {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<z.infer<ReturnType<typeof registerJobSeekerSchema>>>({
    resolver: zodResolver(registerJobSeekerSchema(formValidationMessages)),
    defaultValues: {
      name: '',
      surname: '',
      register_email: '',
      phone: '',
      password: '',
      password_confirmation: '',
      gdpr: false,
      newsletter: false,
      tos: false,
      'g-recaptcha-response': '',
    },
  });

  const handleSubmit = form.handleSubmit(async data => {
    try {
      await onSubmit({ ...data, show_in_index: true }, 'jobseeker');
    } catch (e) {
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
      }
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit} className="w-full">
        <div
          className={cn(
            'flex w-full flex-col items-start justify-start gap-4 sm:gap-6',
            'ea-register-form__content',
          )}
        >
          {/* Name and Surname */}
          <Field
            control={form.control}
            name="name"
            label={t('text33')}
            placeholder={t('text33')}
            required
            className={{ wrapper: 'w-full' }}
          />
          <Field
            control={form.control}
            name="surname"
            label={t('text34')}
            placeholder={t('text34')}
            required
            className={{ wrapper: 'w-full' }}
          />

          {/* Email and Phone */}

          <Field
            control={form.control}
            name="register_email"
            label={t('text35')}
            placeholder={t('text35')}
            type="email"
            required
            className={{ wrapper: 'w-full' }}
          />
          <Field
            control={form.control}
            name="phone"
            label={t('text36')}
            type="tel"
            required
            className={{ wrapper: 'w-full' }}
          />

          {/* Password Fields */}

          <Field
            control={form.control}
            name="password"
            label={t('text191')}
            placeholder={t('text191')}
            type="password"
            required
            className={{ wrapper: 'w-full' }}
          />
          <Field
            control={form.control}
            name="password_confirmation"
            label={t('text263')}
            placeholder={t('text191')}
            type="password"
            required
            className={{ wrapper: 'w-full' }}
          />
        </div>
        {/* FOOTER */}
        <div
          className={cn('mt-6 flex w-full flex-col items-start gap-4', 'ea-register-form__footer')}
        >
          <FormField
            control={form.control}
            name="tos"
            render={({ field }) => (
              <FormItem
                className={cn(
                  'flex flex-row items-start space-x-3 space-y-0',
                  'ea-register-form__tos-field',
                )}
              >
                <FormControl>
                  <Checkbox
                    ref={field.ref}
                    name={field.name}
                    disabled={field.disabled}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel>
                  {t('text264')}{' '}
                  <Link
                    href={getDefaultUrlForTosOrGdprLocalized(ROUTES?.TOS?.(), locale, 'tos')}
                    className="underline"
                    prefetch={false}
                    target="_blank"
                  >
                    {t('text265')}
                  </Link>
                </FormLabel>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="gdpr"
            render={({ field }) => (
              <FormItem
                className={cn(
                  'flex flex-row items-start space-x-3 space-y-0',
                  'ea-register-form__gdpr-field',
                )}
              >
                <FormControl>
                  <Checkbox
                    ref={field.ref}
                    name={field.name}
                    disabled={field.disabled}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel>
                  <Link
                    href={getDefaultUrlForTosOrGdprLocalized(
                      ROUTES?.GDPR?.() ?? '/',
                      locale,
                      'gdpr',
                    )}
                    className="underline"
                    prefetch={false}
                    target="_blank"
                  >
                    {t('text364')}
                  </Link>
                </FormLabel>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="newsletter"
            render={({ field }) => (
              <FormItem
                className={cn(
                  'flex flex-row items-start space-x-3 space-y-0',
                  'ea-register-form__newsletter-field',
                )}
              >
                <FormControl>
                  <Checkbox
                    ref={field.ref}
                    name={field.name}
                    disabled={field.disabled}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel>{t('text629')}</FormLabel>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'g-recaptcha-response'}
            render={() => (
              <FormItem required className={cn('w-full')}>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            className={cn(
              'h-12 w-full rounded-lg bg-primary text-lg hover:bg-primary/90',
              'ea-register-form__submit-button',
            )}
            isLoading={form.formState.isSubmitting}
          >
            {t('text192')}
          </Button>
        </div>
      </form>
    </Form>
  );
}
